import { useState, useRef } from 'react'
import { File, Folder, ChevronRight, ChevronDown, Plus, Trash2, X, Upload } from 'lucide-react'
import { useStore } from '../store/useStore'

export default function FileTree({ onHide }) {
  const { files, currentFile, setCurrentFile, addFile, deleteFile, includeBase64InContext, setIncludeBase64InContext } = useStore()
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']))
  const [showAddFile, setShowAddFile] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  // Group files by type
  const jsFiles = files.filter(f => f.name.endsWith('.js'))
  const htmlFiles = files.filter(f => f.name.endsWith('.html'))
  const cssFiles = files.filter(f => f.name.endsWith('.css'))
  const otherFiles = files.filter(f => 
    !f.name.endsWith('.js') && 
    !f.name.endsWith('.html') && 
    !f.name.endsWith('.css')
  )

  const handleAddFile = () => {
    if (newFileName.trim()) {
      let fileName = newFileName.trim()
      // Add extension if missing
      if (!fileName.includes('.')) {
        fileName += '.js'
      }
      addFile(fileName)
      setNewFileName('')
      setShowAddFile(false)
    }
  }

  const handleDeleteFile = (fileName, e) => {
    e.stopPropagation()
    if (files.length === 1) {
      alert('Cannot delete the last file')
      return
    }
    if (confirm(`Delete ${fileName}?`)) {
      deleteFile(fileName)
    }
  }

  const processFiles = async (fileList) => {
    const uploadedFiles = Array.from(fileList)
    
    for (const file of uploadedFiles) {
      const reader = new FileReader()
      
      // Check file type
      if (file.type.startsWith('image/')) {
        // Images: Convert to base64 data URL
        reader.onload = (e) => {
          addFile(file.name, e.target.result)
        }
        reader.readAsDataURL(file)
      } else if (file.type.startsWith('audio/') || 
                 file.name.endsWith('.mp3') || 
                 file.name.endsWith('.wav') || 
                 file.name.endsWith('.ogg') ||
                 file.name.endsWith('.m4a')) {
        // Audio: Convert to base64 data URL
        reader.onload = (e) => {
          addFile(file.name, e.target.result)
        }
        reader.readAsDataURL(file)
      } else {
        // Text files: Read as text
        reader.onload = (e) => {
          addFile(file.name, e.target.result)
        }
        reader.readAsText(file)
      }
    }
  }

  const handleFileUpload = async (event) => {
    await processFiles(event.target.files)
    // Reset the input
    event.target.value = null
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set dragging to false if leaving the entire component
    if (e.currentTarget === e.target) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const droppedFiles = e.dataTransfer.files
    if (droppedFiles.length > 0) {
      await processFiles(droppedFiles)
    }
  }

  const FileItem = ({ file }) => (
    <div
      onClick={() => setCurrentFile(file.name)}
      className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${
        currentFile === file.name 
          ? 'bg-ide-accentMuted text-ide-accent' 
          : 'text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text'
      }`}
    >
      <File size={14} className={currentFile === file.name ? 'text-ide-accent' : 'text-ide-textDim'} />
      <span className="flex-1 text-sm truncate min-w-0" title={file.name}>{file.name}</span>
      {files.length > 1 && (
        <button
          type="button"
          onClick={(e) => handleDeleteFile(file.name, e)}
          className="opacity-0 group-hover:opacity-100 text-ide-textDim hover:text-ide-error transition-colors"
          aria-label={`Delete ${file.name}`}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  )

  const FolderSection = ({ title, files, icon }) => {
    if (files.length === 0) return null
    
    return (
      <div className="mb-2">
        <div className="flex items-center gap-2 px-3 py-1.5 text-ide-textDim text-xs font-medium uppercase tracking-wider">
          {icon}
          <span>{title}</span>
        </div>
        {files.map(file => (
          <div key={file.name} className="group">
            <FileItem file={file} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div 
      className={`bg-ide-panel flex flex-col h-full w-full min-w-0 relative ${
        isDragging ? 'ring-2 ring-ide-accent ring-inset' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-ide-border">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Folder size={14} className="flex-shrink-0 text-ide-textMuted" />
          <span className="text-sm font-medium text-ide-text truncate">Files</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowAddFile(true)}
            className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
            title="Add file"
            aria-label="Add file"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
            title="Upload file"
            aria-label="Upload file"
          >
            <Upload size={14} />
          </button>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Hide file tree"
              aria-label="Hide file tree"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      
      {/* Hidden file input for upload */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".txt,.json,.md,.csv,.xml,.yaml,.yml,.png,.jpg,.jpeg,.gif,.svg,.bmp,.ico,.webp,.mp3,.wav,.ogg,.m4a,.aac,.flac"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* Add File Form */}
      {showAddFile && (
        <div className="px-3 py-2 bg-ide-elevated border-b border-ide-border">
          <input
            type="text"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddFile()
              if (e.key === 'Escape') setShowAddFile(false)
            }}
            placeholder="filename.js"
            className="w-full bg-ide-bg border border-ide-border rounded px-2 py-1.5 text-sm text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleAddFile}
              className="flex-1 bg-ide-accent hover:bg-ide-accentHover text-white text-xs py-1.5 rounded transition-colors"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowAddFile(false)}
              className="flex-1 bg-ide-surface hover:bg-ide-surfaceHover text-ide-text text-xs py-1.5 rounded border border-ide-border transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-ide-bg/95 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <Upload size={40} className="mx-auto mb-3 text-ide-accent animate-bounce" />
            <p className="text-ide-text text-base font-medium">Drop files here</p>
            <p className="text-ide-textMuted text-sm mt-1">Images, Audio, Text, and more</p>
          </div>
        </div>
      )}

      {/* File List */}
      <div className="flex-1 overflow-y-auto py-2 relative">
        {/* JavaScript Files */}
        <FolderSection
          title="JavaScript"
          files={jsFiles}
          icon={<File size={12} />}
        />

        {/* HTML Files */}
        <FolderSection
          title="HTML"
          files={htmlFiles}
          icon={<File size={12} />}
        />

        {/* CSS Files */}
        <FolderSection
          title="CSS"
          files={cssFiles}
          icon={<File size={12} />}
        />

        {/* Other Files (txt, json, md, etc.) */}
        <FolderSection
          title="Other Files"
          files={otherFiles}
          icon={<File size={12} />}
        />

        {/* Empty State */}
        {files.length === 0 && (
          <div className="px-3 py-8 text-center text-ide-textDim text-sm">
            No files yet
            <br />
            <button
              type="button"
              onClick={() => setShowAddFile(true)}
              className="text-ide-accent hover:underline mt-2"
            >
              Add your first file
            </button>
          </div>
        )}
      </div>

      {/* Binary Data Toggle */}
      <label className="flex items-center gap-2 px-3 py-1.5 border-t border-ide-border text-xs text-ide-textDim cursor-pointer hover:bg-ide-surface transition-colors">
        <input 
          type="checkbox" 
          checked={includeBase64InContext} 
          onChange={(e) => setIncludeBase64InContext(e.target.checked)}
          className="w-3 h-3 rounded border-ide-border accent-ide-accent"
        />
        Include binary for AI
      </label>

      {/* File Count */}
      <div className="px-3 py-1.5 border-t border-ide-border text-xs text-ide-textDim">
        {files.length} file{files.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}
